import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  LimitControlCreateRequest,
  LimitControlUpdateRequest,
  ResponseDenominators,
  ResponseLimitControl,
  ResponseLimitControlKV,
  ResponseLimitTypes,
  ResponsePortfolio,
  ResponseSubLimitTypes
} from '../../domain/entities/LimitControlEntity';
import { limitKeys, limitRepository } from '../../infrastructure/LimitControlConstants';

export const useGetLimits = () => {
  return useQuery<ResponseLimitControlKV, Error>({
    queryKey: limitKeys.all,
    queryFn: () => limitRepository.getAllLimitControls(),
  });
}

export const useGetLimitControlById = (id: string) => {
  return useQuery<ResponseLimitControlKV, Error>({
    queryKey: limitKeys.details(id),
    queryFn: () => limitRepository.getLimitControlById(id),
    enabled: !!id,
  });
}

export const useCreateLimitControl = () => {
  const queryClient = useQueryClient();

  return useMutation<ResponseLimitControl, Error, LimitControlCreateRequest>({
    mutationFn: (limit) => limitRepository.createLimitControl(limit),
    onSuccess: () => {

      queryClient.invalidateQueries({ queryKey: limitKeys.all });
    },
  });
}

export const useUpdateLimitControl = () => {
  const queryClient = useQueryClient();

  return useMutation<ResponseLimitControl, Error, LimitControlUpdateRequest>({
    mutationFn: (limit) => limitRepository.updateLimitControl(limit),
    onSuccess: (_, variables) => {

      queryClient.invalidateQueries({ queryKey: limitKeys.all });
      if (variables.id) {
        queryClient.invalidateQueries({ queryKey: limitKeys.details(variables.id) });
      }
    },
  });
}

export const useDeleteLimitControl = () => {
  const queryClient = useQueryClient();

  return useMutation<ResponseLimitControl, Error, string>({
    mutationFn: (id) => limitRepository.deleteLimitControl(id),
    onSuccess: (_, id) => {

      queryClient.invalidateQueries({ queryKey: limitKeys.all });

      queryClient.removeQueries({ queryKey: limitKeys.details(id) });
    },
  });
}

export const useGetLimitTypes = () => {
  return useQuery<ResponseLimitTypes, Error>({
    queryKey: limitKeys.allTypes,
    queryFn: () => limitRepository.getAllLimitTypes(),
  });
}

export const useGetSubLimitTypes = () => {
  return useQuery<ResponseSubLimitTypes, Error>({
    queryKey: limitKeys.allSubTypesByType,
    queryFn: () => limitRepository.getAllSubLimitTypes(),
  });
}

export const useGetPortfolio = () => {
  return useQuery<ResponsePortfolio, Error>({
    queryKey: limitKeys.allPortfolio,
    queryFn: () => limitRepository.getAllPortfolios(),
  });
}

export const useGetDenominator = () => {
  return useQuery<ResponseDenominators, Error>({
    queryKey: limitKeys.allDenominator,
    queryFn: () => limitRepository.getAllDenominators(),
  });
}
